import {
  Autocomplete,
  autocompleteClasses,
  Box,
  ListSubheader,
  MenuItem,
  styled,
  TextField,
  Typography,
} from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { useGetCustomerLocalities } from 'src/api/localities'
import { useGetEquipment, invalidateMainComponentCache } from 'src/api/main-component'
import { useGetAllMooringsByLocalityId } from 'src/api/moorings'
import { invalidatePositionCache, useGetPositionsForDropDownByLocality } from 'src/api/positions'
import { useMoveROVVideo } from 'src/api/rov-videos'
import { IROVVideoMinimal, ROVVideoTargetType } from 'src/generated-types/rovvideo'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { formatItemIdentification, getMooringName } from 'src/utils/function'
import { getTranslatedText } from 'src/utils/translations'
import { CancelButton } from '../../components/CancelButton'
import { ShowDialogProps } from '../../show-dialog'
import { Button } from '@mui/material'

interface MoveROVVideoDialogProps extends ShowDialogProps {
  rovvideo: IROVVideoMinimal
  customerId: EntityId
  // The locality the video sits on now. The target may be another one.
  localityId: EntityId
  mooringId?: EntityId
  positionId?: EntityId
  mainComponentId?: EntityId
}

interface MoveTarget {
  id: EntityId
  mooringId?: number
  group?: string
  label: string
  // What the list is ordered by within its group, kept apart from the label.
  sortKey: string
}

// Positions and serial numbers are numbers written as text, so "9" has to sort before "10".
const byNumberInText = (a: MoveTarget, b: MoveTarget) =>
  (a.group ?? '').localeCompare(b.group ?? '', undefined, { numeric: true, sensitivity: 'base' }) ||
  (a.mooringId ?? 0) - (b.mooringId ?? 0) ||
  a.sortKey.localeCompare(b.sortKey, undefined, { numeric: true, sensitivity: 'base' })

/**
 * Moves a ROV video that was uploaded on the wrong place. Only where the video hangs changes: the
 * film itself, its title and its category stay as they are.
 */
const MoveROVVideoDialog: React.FC<MoveROVVideoDialogProps> = ({
  pop,
  rovvideo,
  customerId,
  localityId,
  mooringId,
  positionId,
  mainComponentId,
}) => {
  const { t } = useTranslation(['rov', 'common'])

  const [targetLocalityId, setTargetLocalityId] = useState<EntityId>(localityId)
  const [targetType, setTargetType] = useState<ROVVideoTargetType>(
    mainComponentId ? ROVVideoTargetType.MainComponent : ROVVideoTargetType.Position,
  )
  const [targetId, setTargetId] = useState<number | ''>('')

  const { data: localities } = useGetCustomerLocalities({ clientId: customerId })
  // Cages are not offered, the same as when a video is uploaded: a video of a cage goes on its net.
  const { data: positions } = useGetPositionsForDropDownByLocality(customerId, targetLocalityId)
  const { data: moorings } = useGetAllMooringsByLocalityId(customerId, targetLocalityId)
  const { data: equipments } = useGetEquipment(customerId, { localityId: targetLocalityId })

  const { mutateAsync: moveROVVideo, isLoading } = useMoveROVVideo(customerId, localityId, rovvideo.id)

  const positionOptions = useMemo<MoveTarget[]>(
    () =>
      (positions ?? [])
        .map(position => {
          const reference = position.reference?.trim()
          const mooringName = moorings?.find(mooring => mooring.id === position.mooringId)?.name
          return {
            id: position.id,
            mooringId: position.mooringId,
            group: mooringName ? getMooringName(mooringName) : '',
            // Named as in the upload dialog: the position number, and its reference where one is set.
            label: reference ? `${position.name} (${reference})` : position.name,
            sortKey: reference || position.name,
          }
        })
        .sort(byNumberInText),
    [positions, moorings],
  )

  const equipmentOptions = useMemo<MoveTarget[]>(
    () =>
      (equipments ?? [])
        .map(equipment => ({
          id: equipment.id,
          // The type name is stored as {"en":…,"no":…}, which this resolves to the chosen language.
          // The reference is left out; the serial number is what identifies the equipment here.
          label: formatItemIdentification({
            itemType: (equipment.type?.name ? getTranslatedText(equipment.type.name) : '') || equipment.typeName,
            serialNumber: equipment.serialNumber,
          }),
          sortKey: equipment.serialNumber ?? '',
        }))
        .sort(byNumberInText),
    [equipments],
  )

  const options = targetType === ROVVideoTargetType.Position ? positionOptions : equipmentOptions
  const selectedTarget = options.find(option => option.id === targetId) ?? null

  // Moving a video onto the place it already sits is the one choice the API rejects.
  const isUnchanged =
    targetId !== '' &&
    ((targetType === ROVVideoTargetType.Position && targetId === positionId) ||
      (targetType === ROVVideoTargetType.MainComponent && targetId === mainComponentId))

  const onLocalityChange = (value: EntityId) => {
    setTargetLocalityId(value)
    setTargetId('')
  }

  const onTargetTypeChange = (value: ROVVideoTargetType) => {
    setTargetType(value)
    setTargetId('')
  }

  const onMove = async () => {
    if (targetId === '' || isUnchanged) return

    await moveROVVideo({ targetType, targetId: targetId as number })

    // The video leaves one list and joins another, so both ends are refreshed.
    invalidatePositionCache.getPositionROVVideos(customerId, localityId, mooringId as EntityId, positionId)
    if (mainComponentId) {
      invalidateMainComponentCache.getMainComponentROVVideos(customerId, mainComponentId)
      invalidationActivityLogCache.getMainComponentActivityLogs({ mainComponentId })
    }

    if (targetType === ROVVideoTargetType.Position) {
      const target = positionOptions.find(option => option.id === targetId)
      invalidatePositionCache.getPositionROVVideos(
        customerId,
        targetLocalityId,
        target?.mooringId as EntityId,
        targetId as EntityId,
      )
    } else {
      invalidateMainComponentCache.getMainComponentROVVideos(customerId, targetId as EntityId)
      invalidationActivityLogCache.getMainComponentActivityLogs({ mainComponentId: targetId as EntityId })
    }

    toast.success(t('videoMoved'))
    pop(true)
  }

  return (
    <Box className="flex flex-col gap-4 pt-2">
      <Typography variant="body2">{t('moveVideoDescription')}</Typography>

      {/* A customer can have a long list of localities and of equipment, so both can be typed in. */}
      <Autocomplete
        fullWidth
        size="small"
        options={localities ?? []}
        value={(localities ?? []).find(locality => locality.id === targetLocalityId) ?? null}
        getOptionLabel={locality => locality.name ?? ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, value) => value && onLocalityChange(value.id)}
        renderInput={params => <TextField {...params} label={t('locality', { ns: 'common' })} />}
      />

      <TextField
        select
        fullWidth
        size="small"
        label={t('moveVideoTo')}
        value={targetType}
        onChange={event => onTargetTypeChange(Number(event.target.value) as ROVVideoTargetType)}
      >
        <MenuItem value={ROVVideoTargetType.Position}>{t('targetPosition')}</MenuItem>
        <MenuItem value={ROVVideoTargetType.MainComponent}>{t('targetEquipment')}</MenuItem>
      </TextField>

      <Autocomplete
        fullWidth
        size="small"
        options={options}
        value={selectedTarget}
        groupBy={targetType === ROVVideoTargetType.Position ? option => String(option.mooringId ?? '') : undefined}
        renderGroup={params => (
          <Box component="li" key={params.key}>
            <MooringGroupHeader component="div" className={autocompleteClasses.groupLabel}>
              {positionOptions.find(option => String(option.mooringId ?? '') === params.group)?.group}
            </MooringGroupHeader>
            <MooringGroupItems className={autocompleteClasses.groupUl}>{params.children}</MooringGroupItems>
          </Box>
        )}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, value) => setTargetId((value?.id as number) ?? '')}
        noOptionsText={t('noTargetsOnLocality')}
        renderInput={params => (
          <TextField
            {...params}
            label={targetType === ROVVideoTargetType.Position ? t('targetPosition') : t('targetEquipment')}
            error={isUnchanged}
            helperText={isUnchanged ? t('videoAlreadyHere') : undefined}
          />
        )}
      />

      <Box className="flex justify-end gap-4 pt-2">
        <Button variant="contained" onClick={onMove} disabled={targetId === '' || isUnchanged || isLoading}>
          {t('moveVideo')}
        </Button>
        <CancelButton onClick={() => pop()}>{t('cancel', { ns: 'common' })}</CancelButton>
      </Box>
    </Box>
  )
}

export default MoveROVVideoDialog

const MooringGroupHeader = styled(ListSubheader)(({ theme }) => ({
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  top: '-8px',
})) as typeof ListSubheader

const MooringGroupItems = styled('ul')({
  padding: 0,
  [`& .${autocompleteClasses.option}`]: { paddingLeft: '24px' },
})
